import { Alert, StyledReactSelect } from '@fountain/fountain-ui-components';
import { Typography } from '@material-ui/core';
import {
  OfferLetterTemplateForStage,
  OfferLetterTemplatesStage as OfferLetterTemplatesStageAttrs,
  WorkflowDocumentSigningStage,
  WorkflowStageDetail,
} from 'api-clients/monolith';
import produce from 'immer';
import React, { Dispatch, SetStateAction, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import { CONTRACT_TYPE_LABELS } from 'containers/OfferLetterTemplates/constants';
import { ContractType } from 'containers/OfferLetterTemplates/types';
import { StageSettingCard } from 'containers/WorkflowEditor/components/StageSettingCard';
import { StageDetailPatchResponse } from 'containers/WorkflowEditor/contexts/stageContext';

import { collectGroupErrorMessages } from '../Documents/errorUtils';
import { messages } from './messages';
import { useStyles } from './styles';

export interface OfferLetterTemplatesProps {
  stage: WorkflowDocumentSigningStage;
  setStage: Dispatch<SetStateAction<WorkflowStageDetail>>;
  updateStageResult: StageDetailPatchResponse;
}

// The fixed contract types (single source of truth: CONTRACT_TYPE_LABELS) plus,
// only when relevant, an "unspecified" bucket for templates with no
// contract_type set — mirrors the backend's DocumentSigningStage
// #offer_letter_templates_unique_per_contract_type validation, which treats nil
// as its own conflicting bucket too.
const CONTRACT_TYPES = Object.keys(CONTRACT_TYPE_LABELS) as ContractType[];

interface SelectOption {
  label: string;
  value: number | null;
}

export const OfferLetterTemplates: VFC<OfferLetterTemplatesProps> = ({
  stage,
  setStage,
  updateStageResult,
}) => {
  const intl = useIntl();
  const styles = useStyles();
  const { accountSlug } = useParams<{ accountSlug: string }>();

  const availableTemplates: OfferLetterTemplateForStage[] =
    stage.additional_info.available_offer_letter_templates ?? [];
  const assignments: OfferLetterTemplatesStageAttrs[] =
    stage.additional_info.offer_letter_templates_stages_attributes ?? [];

  const hasUnspecifiedTemplates = availableTemplates.some(
    template => !template.contract_type,
  );
  const buckets: (ContractType | null)[] = hasUnspecifiedTemplates
    ? [...CONTRACT_TYPES, null]
    : CONTRACT_TYPES;

  const templateIdsForBucket = (bucket: ContractType | null): number[] =>
    availableTemplates
      .filter(template => (template.contract_type ?? null) === bucket)
      .map(template => template.id)
      .filter((id): id is number => id != null);

  const selectedTemplateIdForBucket = (
    bucket: ContractType | null,
  ): number | null => {
    const bucketTemplateIds = new Set(templateIdsForBucket(bucket));
    const active = assignments.find(
      assignment =>
        !assignment._destroy &&
        assignment.offer_letter_template_id != null &&
        bucketTemplateIds.has(assignment.offer_letter_template_id),
    );
    return active?.offer_letter_template_id ?? null;
  };

  const handleChange =
    (bucket: ContractType | null) => (option: SelectOption | null) => {
      setStage(
        produce(stage, draftStage => {
          const bucketTemplateIds = new Set(templateIdsForBucket(bucket));
          const currentAttrs =
            draftStage.additional_info
              .offer_letter_templates_stages_attributes ?? [];

          const cleaned = currentAttrs.reduce<OfferLetterTemplatesStageAttrs[]>(
            (acc, assignment) => {
              const belongsToBucket =
                !assignment._destroy &&
                assignment.offer_letter_template_id != null &&
                bucketTemplateIds.has(assignment.offer_letter_template_id);

              if (!belongsToBucket) {
                acc.push(assignment);
                return acc;
              }

              // Persisted assignments need `_destroy` to remove server-side;
              // ones added client-side this session (no `id` yet) can just
              // be dropped from the array.
              if (assignment.id != null) {
                acc.push({ ...assignment, _destroy: true });
              }
              return acc;
            },
            [],
          );

          if (option?.value != null) {
            cleaned.push({ offer_letter_template_id: option.value });
          }

          draftStage.additional_info.offer_letter_templates_stages_attributes =
            cleaned;
        }),
      );
    };

  const errors =
    (updateStageResult.isError && updateStageResult.error?.errors) || undefined;
  const conflictErrorMessages = collectGroupErrorMessages(errors, key =>
    key.startsWith('offer_letter_templates_stages'),
  );

  const bucketLabel = (bucket: ContractType | null): string =>
    bucket
      ? intl.formatMessage(CONTRACT_TYPE_LABELS[bucket])
      : intl.formatMessage(messages.noContractType);

  return (
    <StageSettingCard
      title={<FormattedMessage {...messages.offerLetterTemplates} />}
      variant="default"
    >
      {conflictErrorMessages.length > 0 && (
        <Alert fullWidth type="danger" className={styles.errorsAlert}>
          <span>{conflictErrorMessages.join(', ')}</span>
        </Alert>
      )}
      {availableTemplates.length === 0 ? (
        <Typography variant="body2">
          <FormattedMessage
            {...messages.noTemplatesYet}
            values={{
              link: (
                <Link
                  className={styles.noTemplatesLink}
                  to={`/${accountSlug}/offer_letter_templates`}
                >
                  <FormattedMessage {...messages.createTemplatesLink} />
                </Link>
              ),
            }}
          />
        </Typography>
      ) : (
        buckets.map(bucket => {
          const options: SelectOption[] = [
            { label: intl.formatMessage(messages.none), value: null },
            ...availableTemplates
              .filter(template => (template.contract_type ?? null) === bucket)
              .map(template => ({
                label: template.title ?? '',
                value: template.id ?? null,
              })),
          ];
          const selectedId = selectedTemplateIdForBucket(bucket);

          return (
            <div className={styles.row} key={bucket ?? 'unspecified'}>
              <Typography className={styles.contractTypeLabel} variant="body2">
                {bucketLabel(bucket)}
              </Typography>
              <StyledReactSelect
                className={styles.select}
                aria-label={intl.formatMessage(
                  messages.selectTemplateAriaLabel,
                  { contractType: bucketLabel(bucket) },
                )}
                onChange={handleChange(bucket)}
                options={options}
                value={
                  options.find(option => option.value === selectedId) ??
                  options[0]
                }
              />
            </div>
          );
        })
      )}
    </StageSettingCard>
  );
};
